import { useMemo, useState, useEffect } from 'react';
import { Box, Divider, IconButton, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import Iconify from 'src/components/iconify';
import { useCompetitiveIntelligenceContext } from './CompetitiveIntelligenceProvider';

type Term = {
    label: string;
    score: number; // 0-100
};

interface TopAttackedSearchTermsProps {
    terms?: Term[];
    loading?: boolean;
    barColor?: string;
    onSelectTerm?: (term: string) => void;
}

// Lightweight component showing top attacked search terms with compact/expanded views
export default function TopAttackedSearchTerms(props: TopAttackedSearchTermsProps) {
    const { terms, loading = false, barColor = '#C09910', onSelectTerm } = props;

    const { selectedCountry, filterStore } = useCompetitiveIntelligenceContext();
    const countriesPayload: string[] = useMemo(() => {
        if (selectedCountry) return [selectedCountry];
        if (Array.isArray(filterStore?.countries) && filterStore.countries.length > 0) return filterStore.countries;
        return [];
    }, [selectedCountry, filterStore?.countries]);

    // Placeholder data while API integration is wired
    const placeholderTerms: Term[] = useMemo(
        () => [
            { label: 'sac polene', score: 96 },
            { label: 'clutch wallet', score: 92 },
            { label: 'crossbody purse', score: 78 },
            { label: 'duffle bag', score: 89 },
            { label: 'tote bag', score: 85 },
        ],
        []
    );

    // Map API response to Term[] with normalized score (0-100 based on max searches)
    const fetchedTerms: Term[] = useMemo(() => {
        const keywords = filterStore?.loadedKeywords as Array<{
            search_term: string;
            total_organic_searches: number;
        }> | undefined;
        if (!keywords || keywords.length === 0) return [];
        const maxValue = Math.max(
            0,
            ...keywords.map((k) => (typeof k.total_organic_searches === 'number' ? k.total_organic_searches : 0))
        );
        if (maxValue <= 0) return keywords.map((k) => ({ label: k.search_term, score: 0 }));
        return keywords.map((k) => ({
            label: k.search_term,
            score: Math.round(((typeof k.total_organic_searches === 'number' ? k.total_organic_searches : 0) * 100) / maxValue),
        }));
    }, [filterStore?.loadedKeywords]);

    const isLoading = loading;
    const data = terms && terms.length > 0 ? terms : (fetchedTerms.length > 0 ? fetchedTerms : placeholderTerms);
    const filteredData = useMemo(() => (data || []).filter((t) => (t?.score ?? 0) >= 1), [data]);
    const [expanded, setExpanded] = useState<boolean>(false);

    return (
        <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontSize: "13px",
                        fontWeight: "700",
                    }}
                >Top attacked search terms</Typography>
                <IconButton size="small" onClick={() => setExpanded((e) => !e)} aria-label={expanded ? 'Collapse' : 'Expand'}>
                    <Iconify icon={expanded ? 'eva:collapse-outline' : 'eva:expand-outline'} width={18} />
                </IconButton>
            </Stack>

            <Divider sx={{ mb: 1 }} />

            {expanded ? (
                <ExpandedView data={filteredData} barColor={barColor} loading={isLoading} onSelect={onSelectTerm} />
            ) : (
                <CompactView data={filteredData} barColor={barColor} loading={isLoading} onSelect={onSelectTerm} />
            )}
        </Paper>
    );
}

function CompactView({ data, barColor = '#C09910', loading, onSelect }: { data: Term[]; barColor?: string; loading?: boolean; onSelect?: (s: string) => void }) {
    const [page, setPage] = useState<number>(0);
    const pageSize = 5;
    const totalPages = Math.max(1, Math.ceil((data?.length ?? 0) / pageSize));

    useEffect(() => {
        setPage(0);
    }, [data]);

    const start = page * pageSize;
    const visible = data.slice(start, start + pageSize);
    const showArrows = (data?.length ?? 0) > pageSize;

    return (
        showArrows ? (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ pb: 0.5 }}>
                <IconButton size="small" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} aria-label="Previous keywords page">
                    <Iconify icon="eva:arrow-ios-back-outline" width={18} />
                </IconButton>
                <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${visible.length}, 1fr)`, alignItems: 'center', columnGap: 2, flex: 1 }}>
                    {visible.map((t, idx) => (
                        <Box
                            key={`${t.label}-${start + idx}`}
                            onClick={onSelect ? () => onSelect(t.label) : undefined}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1,
                                cursor: onSelect ? 'pointer' : 'default',
                                ...(idx > 0 && { borderLeft: (theme) => `1px solid ${theme.palette.divider}` }),
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', fontSize: "13px", fontWeight: "400" }}
                                title={t.label}
                            >
                                {t.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ flex: '0 0 auto', fontSize: "11px", fontWeight: "400" }}>
                                {t.score}%
                            </Typography>
                            <Box sx={{ height: 28, width: 8, borderRadius: 999, bgcolor: 'background.neutral', overflow: 'hidden', position: 'relative' }}>
                                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${t.score}%`, bgcolor: barColor }} />
                            </Box>
                        </Box>
                    ))}
                </Box>
                <IconButton size="small" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} aria-label="Next keywords page">
                    <Iconify icon="eva:arrow-ios-forward-outline" width={18} />
                </IconButton>
            </Stack>
        ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${visible.length}, 1fr)`, alignItems: 'center', columnGap: 2, pb: 0.5 }}>
                {visible.map((t, idx) => (
                    <Box
                        key={`${t.label}-${idx}`}
                        onClick={onSelect ? () => onSelect(t.label) : undefined}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1,
                            cursor: onSelect ? 'pointer' : 'default',
                            ...(idx > 0 && { borderLeft: (theme) => `1px solid ${theme.palette.divider}` }),
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', fontSize: "13px", fontWeight: "400" }}
                            title={t.label}
                        >
                            {t.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flex: '0 0 auto', fontSize: "11px", fontWeight: "400" }}>
                            {t.score}%
                        </Typography>
                        <Box sx={{ height: 28, width: 8, borderRadius: 999, bgcolor: 'background.neutral', overflow: 'hidden', position: 'relative' }}>
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${t.score}%`, bgcolor: barColor }} />
                        </Box>
                    </Box>
                ))}
            </Box>
        )
    );
}

function ExpandedView({ data, barColor = '#C09910', loading, onSelect }: { data: Term[]; barColor?: string; loading?: boolean; onSelect?: (s: string) => void }) {
    return (
        <Box>
            <Stack spacing={1.25}>
                {data.map((t) => (
                    <Stack key={t.label} direction="row" alignItems="center" spacing={2}>
                        <Typography variant="body2" sx={{ minWidth: 160, flexShrink: 0 }}>{t.label}</Typography>
                        <Box sx={{ flex: 1 }}>
                            <LinearProgress
                                variant="determinate"
                                value={t.score}
                                sx={{ height: 8, borderRadius: 999, bgcolor: 'background.neutral', '& .MuiLinearProgress-bar': { backgroundColor: barColor } }} />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40, textAlign: 'right' }}>{t.score}%</Typography>
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
}


